import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaCloud, FaGlobe } from 'react-icons/fa';

const partners = [
  {
    src: 'https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg',
    alt: 'AWS Partner',
    name: 'Amazon Web Services',
    caption: 'Scalable infrastructure for uptime, storage, and secure delivery.',
  },
  {
    src: 'https://www.graphicdesignforum.com/uploads/default/optimized/2X/b/b712897679bee9a9cd8ecdca0d17a6ffc7cc3c98_2_1380x626.jpeg',
    alt: 'GoDaddy Partner',
    name: 'GoDaddy',
    caption: 'Domain and web presence support for school and platform visibility.',
  },
];

const trustPills = [
  { icon: <FaCloud />, label: 'Cloud-ready delivery' },
  { icon: <FaGlobe />, label: 'Live website support' },
  { icon: <FaCheckCircle />, label: 'Secure public access' },
];

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: 1.4rem 1rem 1.8rem;
`;

const Shell = styled.div`
  max-width: 1180px;
  margin: 0 auto;
  padding: 1.4rem;
  border-radius: 32px;
  background:
    radial-gradient(circle at 12% 0%, rgba(249, 115, 22, 0.12), transparent 28%),
    radial-gradient(circle at 100% 10%, rgba(59, 130, 246, 0.12), transparent 30%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(255, 250, 245, 0.98) 100%);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);

  @media (min-width: 768px) {
    padding: 1.8rem;
  }
`;

const Header = styled.div`
  text-align: center;
  max-width: 740px;
  margin: 0 auto 1.5rem;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.8rem;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  color: #be123c;
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Heading = styled.h2`
  margin: 0.95rem 0 0;
  font-size: clamp(1.7rem, 3vw, 2.4rem);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #111827;
`;

const Description = styled.p`
  margin: 0.75rem 0 0;
  color: #475569;
  line-height: 1.7;
  font-size: 0.98rem;
`;

const Pills = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.7rem;
  margin-top: 1rem;
`;

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.65rem 0.9rem;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.18);
  color: #334155;
  font-size: 0.86rem;
  font-weight: 700;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.05);
`;

const PartnerGrid = styled.div`
  display: grid;
  gap: 1rem;

  @media (min-width: 860px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const PartnerCard = styled(motion.div)`
  display: grid;
  gap: 1rem;
  align-items: center;
  padding: 1.15rem;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);

  @media (min-width: 600px) {
    grid-template-columns: 160px 1fr;
  }
`;

const LogoWrap = styled.div`
  min-height: 88px;
  border-radius: 20px;
  display: grid;
  place-items: center;
  padding: 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
`;

const Logo = styled.img`
  max-width: 100%;
  max-height: 54px;
  object-fit: contain;
`;

const PartnerName = styled.div`
  font-size: 1.08rem;
  font-weight: 800;
  color: #111827;
`;

const PartnerCaption = styled.p`
  margin: 0.35rem 0 0;
  color: #475569;
  line-height: 1.6;
  font-size: 0.92rem;
`;

const ScrollingCustomers = () => {
  return (
    <Section>
      <Shell>
        <Header>
          <Eyebrow>
            <FaCheckCircle />
            Trusted Delivery Stack
          </Eyebrow>
          <Heading>Built on dependable partners that support real school operations</Heading>
          <Description>
            Skhool is designed for daily usage, not just demos. Infrastructure, domain delivery, and public access are
            backed by partners that help the platform stay stable, visible, and ready for growth.
          </Description>
          <Pills>
            {trustPills.map((pill) => (
              <Pill key={pill.label}>
                {pill.icon}
                {pill.label}
              </Pill>
            ))}
          </Pills>
        </Header>

        <PartnerGrid>
          {partners.map((partner, index) => (
            <PartnerCard
              key={partner.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: index * 0.08 }}
              whileHover={{ y: -4 }}
            >
              <LogoWrap>
                <Logo src={partner.src} alt={partner.alt} title={partner.name} />
              </LogoWrap>
              <div>
                <PartnerName>{partner.name}</PartnerName>
                <PartnerCaption>{partner.caption}</PartnerCaption>
              </div>
            </PartnerCard>
          ))}
        </PartnerGrid>
      </Shell>
    </Section>
  );
};

export default ScrollingCustomers;
